// What one override rule actually does, stated on the rule.
//
// This is the whole "rules only" impact treatment. The alternative designs, a
// Sankey and a before/after matrix, both drew a picture of a movement that is
// typically about one percent of the population, which meant a diagram whose
// largest ribbon was noise. What a person wants to know about a rule is answered
// by the rule: how many leads it moves, and whether any of them convert.
//
// Two numbers because the difference matters. A rule can decide the segment for
// thousands of leads and change none of them, which means it is restating what
// the trees already said. That is worth seeing, and it is invisible in any chart
// of the shipped distribution.
import React from 'react';

import { Threshold } from '../../../models/ModelPerformances';
import { SEGMENT_LABEL } from '../trees/nodeSegment';
import FitStars from './FitStars';
import { RuleImpact } from './overrideImpactApi';
import './ruleImpactNote.css';

const count = (value: number) => value.toLocaleString('en-US');

/**
 * Said in a tooltip rather than in the chip, because it qualifies every one of
 * these notes and repeating it on each would crowd out the number.
 *
 * "Training", not "validation": `getOverrideImpact` scores
 * `getDataSetTableName('training', 'customer_fit')`. The note used to say
 * validation set, which named a dataset these counts were never taken from.
 *
 * A rule with no row at all is the ambiguous case, and the tooltip is where that
 * ambiguity belongs: nothing matched its conditions, OR a higher-priority rule
 * of the same kind matched the same leads first and decided them. From the
 * outside those look identical, and claiming either one would be a guess.
 */
const MEASURED_ON =
  'Measured on the training set. A rule with no leads either matches nothing, ' +
  'or a higher-priority rule of the same kind reached them first.';

/**
 * Share of the population, to one decimal below 10% and whole above it.
 *
 * A rule typically moves well under one percent, and rounding that to a whole
 * number renders every rule in the model as "0% of leads", which reads as
 * nothing happening rather than as a small number.
 */
const share = (moved: number, total: number) => {
  const percent = (100 * moved) / total;
  if (percent > 0 && percent < 0.1) return '<0.1%';
  return `${percent >= 10 ? Math.round(percent) : percent.toFixed(1)}%`;
};

function RuleImpactNote({
  impact,
  segment,
  total,
  totalConversions,
}: {
  impact?: RuleImpact;
  /** Where this rule sends the leads it moves, as the rule itself declares. */
  segment?: string;
  /**
   * Population the share is taken against. Omitted when it is not known, in
   * which case the note falls back to counts: a percentage with no stated
   * denominator is the kind of number that gets compared against another one
   * built differently.
   */
  total?: number;
  /**
   * Conversions in the same population, so the second figure can be a share
   * too. A rule that reads "moves 8.0% of leads, carrying 15 conversions" gives
   * two numbers on two different scales and invites the reader to compare them
   * anyway; 15 conversions is large or negligible depending on a denominator
   * that is not on screen.
   */
  totalConversions?: number;
}) {
  // No entry means the rule decided nothing: either nothing matched it, or a
  // higher priority rule of the same kind matched everything it did.
  if (!impact) {
    return (
      <div className="rimp" title={MEASURED_ON}>
        <span className="rimp-chip rimp-chip--idle">
          no leads land on this rule
        </span>
      </div>
    );
  }

  if (impact.moved === 0) {
    return (
      <div className="rimp" title={MEASURED_ON}>
        <span className="rimp-chip rimp-chip--idle">
          matches <b>{count(impact.matched)}</b> leads, changes none of them
        </span>
      </div>
    );
  }

  // "moves 4% of leads to Low", which is the reading that says what the rule is
  // FOR. The count alone says how big it is and not what it does.
  // A tenant can carry a segment name the fit vocabulary does not hold (mqa
  // uses high / very high), so an unknown key falls back to the raw value rather
  // than rendering `undefined`.
  const destination = segment
    ? SEGMENT_LABEL[segment as Threshold] ?? segment
    : null;
  const headline =
    total && total > 0 ? (
      <>
        moves <b>{share(impact.moved, total)}</b> of leads
      </>
    ) : (
      <>
        moves <b>{count(impact.moved)}</b>{' '}
        {impact.moved === 1 ? 'lead' : 'leads'}
      </>
    );

  return (
    <div className="rimp" title={MEASURED_ON}>
      {/* The destination as the marks the product already uses for fit: three
          stars down to a cross, from the same component the lead profile card
          draws. Naming the segment AND showing its rating is not a repetition,
          it is the word and the scale it sits on. */}
      <span className="rimp-chip">
        {headline}
        {destination && (
          <>
            {' to '}
            <FitStars segment={segment} />
            <span className="rimp-dest">{destination}</span>
          </>
        )}
      </span>
      {impact.movedConversions > 0 && (
        <span className="rimp-chip">
          {/* Two sentences, not one sentence with the number swapped. Writing
              the count into the share's wording produced "carrying 15 of
              conversions", which is reachable whenever the leaf grid settles
              without a grid or the training set has no conversions. The
              headline above already branches this way. */}
          {totalConversions && totalConversions > 0 ? (
            <>
              carrying <b>{share(impact.movedConversions, totalConversions)}</b>{' '}
              of conversions
            </>
          ) : (
            <>
              carrying <b>{count(impact.movedConversions)}</b>{' '}
              {impact.movedConversions === 1 ? 'conversion' : 'conversions'}
            </>
          )}
        </span>
      )}
      {impact.matched > impact.moved && (
        <span className="rimp-chip rimp-chip--idle">
          {count(impact.moved)} of {count(impact.matched)} matched
        </span>
      )}
    </div>
  );
}

export default RuleImpactNote;
